import { useState, useCallback } from "react";
import type { Document, DocumentCategory } from "../_types/portal.types";

const API_BASE = "/api";

interface UseDocumentsReturn {
  documents: Document[];
  isLoading: boolean;
  error: string | null;
  fetchDocuments: () => Promise<void>;
  uploadDocument: (
    file: File,
    category: DocumentCategory,
    description?: string
  ) => Promise<{ success: boolean; error?: string; document?: Document }>;
  deleteDocument: (id: string) => Promise<{ success: boolean; error?: string }>;
  downloadDocument: (document: Document) => Promise<void>;
}

export function useDocuments(): UseDocumentsReturn {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/documents`, {
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        throw new Error(data.error || "Failed to fetch documents");
      }

      const data = await response.json() as Document[];
      setDocuments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch documents");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const uploadDocument = useCallback(
    async (file: File, category: DocumentCategory, description?: string) => {
      setIsLoading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", category);
        if (description) {
          formData.append("description", description);
        }

        const response = await fetch(`${API_BASE}/documents/upload`, {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        const data = await response.json() as { data?: Document; error?: string };

        if (!response.ok) {
          return { success: false, error: data.error || "Upload failed" };
        }

        // Add new document to list
        if (data.data) {
          setDocuments((prev) => [data.data!, ...prev]);
        }

        return { success: true, document: data.data };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Upload failed",
        };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const deleteDocument = useCallback(async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/documents/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        return { success: false, error: data.error || "Delete failed" };
      }

      // Remove document from list
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : "Delete failed",
      };
    }
  }, []);

  const downloadDocument = useCallback(async (document: Document) => {
    try {
      const response = await fetch(
        `${API_BASE}/documents/${document.id}/download`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = document.originalFilename;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
    } catch (err) {
      console.error("Download error:", err);
      throw err;
    }
  }, []);

  return {
    documents,
    isLoading,
    error,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    downloadDocument,
  };
}
